import {
  useGetRevenueByDepartmentQuery,
  useGetFOReportsQuery,
  useGetFOOccupancyFlashQuery,
} from '../../redux/services/foreportsApi';
import {useGetBQSalesQuery} from '../../redux/services/bqreportsApi';
import {useGetPOSummQuery} from '../../redux/services/poreportsApi';

export interface ChartSpec {
  type: 'pie' | 'bar';
  labels: string[];
  values: number[];
  tiles?: {label: string; value: string}[];
}

export interface ReportChartEntry {
  key: string;
  title: string;
  /** Any one of these mn_link values in the user's menu grants the card. */
  links: string[];
  /** Mobile screen opened by the card; absent = report exists on web only. */
  screen?: string;
  params: 'range' | 'single';
  useData: (a: {
    cid: string;
    fdt: string;
    tdt: string;
    skip: boolean;
  }) => {data: any; isFetching: boolean; isError: boolean};
  toChart: (data: any) => ChartSpec | null;
}

const num = (v: any) => Number(v) || 0;
const arr = (v: any): any[] => (Array.isArray(v) ? v : []);
const inr = (v: any) =>
  `₹${num(v).toLocaleString('en-IN', {maximumFractionDigits: 0})}`;

const sumBy = (
  rows: any[],
  keyFn: (r: any) => string,
  valFn: (r: any) => number,
) => {
  const m = new Map<string, number>();
  rows.forEach(r => {
    const k = keyFn(r);
    m.set(k, (m.get(k) || 0) + valFn(r));
  });
  return m;
};

const fromMap = (m: Map<string, number>, type: 'pie' | 'bar'): ChartSpec | null => {
  const labels = [...m.keys()].filter(k => (m.get(k) || 0) > 0);
  if (!labels.length) {
    return null;
  }
  return {type, labels, values: labels.map(k => m.get(k) || 0)};
};

export const REPORT_CHARTS: ReportChartEntry[] = [
  {
    key: 'revenue',
    title: 'Revenue by Department',
    links: ['/reports/forevenue'],
    screen: 'RevenueSummary',
    params: 'range',
    useData: ({cid, fdt, tdt, skip}) => {
      const r = useGetRevenueByDepartmentQuery({cid, fdt, tdt}, {skip});
      return {data: r.data, isFetching: r.isFetching, isError: r.isError};
    },
    toChart: data =>
      fromMap(
        sumBy(arr(data), r => r.rv_descr || 'Other', r => num(r.rv_total)),
        'pie',
      ),
  },
  {
    key: 'collection',
    title: 'Collection by Department',
    links: ['/reports/cashier', '/reports/focashiernew', '/reports/focashier'],
    params: 'range',
    useData: ({cid, fdt, tdt, skip}) => {
      const r = useGetFOReportsQuery({cid, fdt, tdt}, {skip});
      return {data: r.data, isFetching: r.isFetching, isError: r.isError};
    },
    toChart: data => {
      // fo cashier returns [cashierRows, cashierTotals, deptSummary]
      const dept = arr(Array.isArray(data) ? data[2] : []);
      const m = new Map<string, number>();
      dept.forEach(d =>
        m.set(
          String(d.dept),
          num(d.cash) + num(d.card) + num(d.bank) + num(d.upi) + num(d.credit),
        ),
      );
      return fromMap(m, 'bar');
    },
  },
  {
    key: 'occupancy',
    title: 'Occupancy',
    links: ['/reports/fooccchart'],
    params: 'single',
    useData: ({cid, tdt, skip}) => {
      const r = useGetFOOccupancyFlashQuery({cid, date: tdt}, {skip});
      return {data: r.data, isFetching: r.isFetching, isError: r.isError};
    },
    toChart: d => {
      if (!d || d.total_rooms === undefined) {
        return null;
      }
      const occupied = num(d.occupied_rooms);
      const vacant = num(d.vacant_rooms);
      const other = Math.max(0, num(d.total_rooms) - occupied - vacant);
      const m = new Map<string, number>([
        ['Occupied', occupied],
        ['Vacant', vacant],
        ['Not for sale', other],
      ]);
      const spec = fromMap(m, 'pie');
      return spec
        ? {
            ...spec,
            tiles: [
              {label: 'Occupancy', value: `${num(d.occupancy_percent).toFixed(1)}%`},
              {label: 'ADR', value: inr(d.adr)},
              {label: 'RevPAR', value: inr(d.revpar)},
            ],
          }
        : null;
    },
  },
  {
    key: 'hallsales',
    title: 'Banquet Sales',
    links: ['/reports/hallsales'],
    params: 'range',
    useData: ({cid, fdt, tdt, skip}) => {
      const r = useGetBQSalesQuery({cid, fdt, tdt}, {skip});
      return {data: r.data, isFetching: r.isFetching, isError: r.isError};
    },
    toChart: data =>
      fromMap(
        sumBy(arr(data), r => r.bl_hallname || 'Hall', r => num(r.bl_total)),
        'bar',
      ),
  },
  {
    key: 'possales',
    title: 'POS Sales by Counter',
    links: ['/reports/polistbills', '/reports/waiterwise', '/reports/itemwise'],
    params: 'range',
    useData: ({cid, fdt, tdt, skip}) => {
      const r = useGetPOSummQuery({cid, fdt, tdt}, {skip});
      return {data: r.data, isFetching: r.isFetching, isError: r.isError};
    },
    toChart: data =>
      fromMap(
        sumBy(arr(data), r => r.bl_areaname || 'Counter', r => num(r.bl_act)),
        'bar',
      ),
  },
];

/** `menu` is `gdata.menu`, already filtered to what the user may view at login. */
export const permittedReportCharts = (menu: any[] | undefined) =>
  REPORT_CHARTS.filter(e => menu?.some(m => e.links.includes(m.mn_link)));
